import type { SingleChoiceOption, TaskCommentSingleChoiceOption } from '@frontend/api-types';
import { type SingleChoiceAnalyticsTopic, useEffect } from '@frontend/react';
import { Text, YStack } from '@frontend/web-react';
import { useAnalytics, useTriggerSingleChoiceOptionMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';

type TaskChatSingleChoiceOptionsProps = {
  analyticsTopic: SingleChoiceAnalyticsTopic;
  commentId: string;
  suggestions: TaskCommentSingleChoiceOption[];
  taskId: string;
};

export const TaskChatSingleChoiceOptions = ({
  analyticsTopic,
  commentId,
  suggestions,
  taskId,
}: TaskChatSingleChoiceOptionsProps) => {
  const [triggerSingleChoiceOption, { isLoading: isTriggerSingleChoiceOptionLoading }] =
    useTriggerSingleChoiceOptionMutation();
  const { track } = useAnalytics();

  useEffect(() => {
    track({
      data: { commentId, optionIds: suggestions.map((suggestion) => suggestion.id), taskId },
      event: 'suggestionBlockShown',
      topic: analyticsTopic,
    });
  }, []);

  const handleSelect = async (option: TaskCommentSingleChoiceOption, selectedOption: SingleChoiceOption) => {
    if (isTriggerSingleChoiceOptionLoading) return;
    track({
      data: { commentId, optionId: option.id, taskId },
      event: 'suggestionOptionClicked',
      topic: analyticsTopic,
    });

    await handleMutation({
      mutation: () =>
        triggerSingleChoiceOption({
          commentId,
          optionId: option.id,
          selectedOption: selectedOption.value,
          taskId,
        }).unwrap(),
      showErrorToast: true,
      showSuccessToast: false,
    });
  };

  return (
    <YStack className="gap-sm py-md">
      {suggestions
        .filter((option) => option.selectedOption === null && option.dismissedAt === null)
        .map((option) => {
          const isDisabled = isTriggerSingleChoiceOptionLoading;

          return (
            <YStack className="gap-sm" key={option.id}>
              {option.options.map((opt) => (
                <button
                  className={`ml-auto rounded-lg border border-cream-200 px-md py-sm text-left transition-opacity ${
                    isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-80'
                  }`}
                  disabled={isDisabled}
                  key={opt.value}
                  onClick={() => {
                    void handleSelect(option, opt);
                  }}
                >
                  <Text className="text-neutral-950" type="p-sm">
                    {opt.label}
                  </Text>
                </button>
              ))}
            </YStack>
          );
        })}
    </YStack>
  );
};
